import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const loadLogoBase64 = async () => {
  const response = await fetch('/images/logosheet.jpg');
  if (!response.ok) {
    return null;
  }

  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const formatExportDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return 'DD MM YYYY';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${day} ${month} ${year}`;
};

const buildExportFilename = (report) => {
  const shiftNumber = Number(report?.shift);
  const shiftLabel = [1, 2, 3].includes(shiftNumber) ? String(shiftNumber) : 'Int';
  return `RGA Daily Shift ${shiftLabel} Report – ${formatExportDate(report?.date)}.pdf`;
};

const exportToPDF = async (report) => {
  const doc = new jsPDF();

  try {
    const logoBase64 = await loadLogoBase64();
    if (logoBase64) {
      doc.addImage(logoBase64, 'JPEG', 10, 10, 72, 17);
    }
  } catch (_) {
    // Continue export if logo fails to load.
  }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`Daily Report for Shift No. ${report.shift}`, 110, 20, { align: 'center' });
  doc.text(report.shiftTime || '', 110, 27, { align: 'center' });
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Prepared By: RGA', 190, 20, { align: 'right' });

  if (Number(report.shift) === 3) {
    const firstDate = new Date(report.date).toLocaleDateString();
    const secondDate = report.secondDate
      ? new Date(report.secondDate).toLocaleDateString()
      : new Date(new Date(report.date).setDate(new Date(report.date).getDate() + 1)).toLocaleDateString();
    doc.text(`Date: ${firstDate}`, 190, 33, { align: 'right' });
    doc.text('to', 190, 39, { align: 'right' });
    doc.text(secondDate, 190, 45, { align: 'right' });
  } else {
    const dateText = new Date(report.date).toLocaleDateString();
    doc.text(`Date: ${dateText}`, 190, 33, { align: 'right' });
  }

  doc.setFont('helvetica', 'normal');
  doc.text(`In Charge: ${report.inCharge || ''}`, 10, 38);

  const headerSpacing = 60;
  let startY = headerSpacing;

  const addSection = (title, headers, data, bgColor) => {
    const borderThickness = 0.2;

    doc.setFillColor(...bgColor);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(borderThickness);
    doc.rect(10, startY - 7, 190, 8, 'FD');
    doc.rect(10, startY - 7, 190, 8);
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(title, 15, startY - 2);

    doc.autoTable({
      startY,
      head: [headers],
      body: data.length > 0 ? data : [['']
        .concat(Array(Math.max(headers.length - 1, 0)).fill(''))],
      styles: {
        fontSize: 10,
        halign: 'center',
        lineWidth: borderThickness,
        lineColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: bgColor,
        textColor: [0, 0, 0],
        halign: 'center',
        lineWidth: borderThickness,
        lineColor: [0, 0, 0]
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      tableLineColor: [0, 0, 0],
      tableLineWidth: borderThickness,
      theme: 'grid',
      margin: { left: 10, right: 10 }
    });

    startY = doc.lastAutoTable.finalY + 10;
  };

  addSection(
    'SCR',
    ['Time', 'No', 'Tag', 'Status', 'Notes'],
    (Array.isArray(report.scr) ? report.scr : []).map((scr) => [
      scr.time || '',
      scr.number ?? '',
      scr.tag || '',
      scr.status || '',
      scr.notes || ''
    ]),
    [30, 136, 229]
  );

  addSection(
    'Microphone Station Group',
    ['Time', 'Area', 'Status', 'Notes'],
    (Array.isArray(report.microphoneStationGroup) ? report.microphoneStationGroup : []).map((msg) => [
      msg.time || '',
      msg.area || '',
      msg.status || '',
      msg.notes || ''
    ]),
    [255, 195, 0]
  );

  addSection(
    'Corrective Maintenance',
    ['Time', 'CM No', 'Status', 'Notes'],
    (Array.isArray(report.correctiveMaintenance) ? report.correctiveMaintenance : []).map((cm) => [
      cm.time || '',
      cm.cmNo || '',
      cm.status || '',
      cm.notes || ''
    ]),
    [102, 204, 0]
  );

  addSection(
    'System Log',
    ['Time', 'Log Type', 'Status', 'Notes'],
    (Array.isArray(report.systemFaultLog) ? report.systemFaultLog : []).map((sfl) => [
      sfl.time || '',
      sfl.logType || '',
      sfl.status || '',
      sfl.notes || ''
    ]),
    [255, 87, 51]
  );

  addSection(
    'PPM',
    ['Time', 'PPM No', 'Status', 'Notes'],
    (Array.isArray(report.ppm) ? report.ppm : []).map((ppm) => [
      ppm.time || '',
      ppm.ppmNo || '',
      ppm.status || '',
      ppm.notes || ''
    ]),
    [255, 195, 0]
  );

  addSection(
    'Others',
    ['Time', 'Area', 'Notes'],
    (Array.isArray(report.others) ? report.others : []).map((other) => [
      other.time || '',
      other.area || '',
      other.notes || ''
    ]),
    [30, 136, 229]
  );

  doc.save(buildExportFilename(report));
};

export default exportToPDF;
